import { Button } from '@/components/ui/shadcn/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/shadcn/table';
import { TableColumnHeader } from '@/components/ui/table-cells/TableColumnHeader';
import { useTranslations } from '@/hooks/useTranslations';
import { daysUntil, formatBytes, formatDate } from '@/lib/adoc/format';
import { cn } from '@/lib/utils';
import type { AdocFileSummary, AdocFilterOption } from '@/types/adoc';
import { Link, router } from '@inertiajs/react';
import { Download, RefreshCw, Trash2 } from 'lucide-react';
import { route } from 'ziggy-js';

interface AdocFileTableProps {
    files: AdocFileSummary[];
    users: AdocFilterOption[];
    sortColumn: string | null;
    sortDirection: 'asc' | 'desc' | null;
    onSortChange: (column: string, direction: 'asc' | 'desc') => void;
    columnFilters: Record<string, string[]>;
    onFilterChange: (column: string, values: string[]) => void;
    canDelete: boolean;
    onRefresh: (file: AdocFileSummary) => void;
    onDelete: (file: AdocFileSummary) => void;
    emptyMessage: string;
}

export function AdocFileTable({
    files,
    users,
    sortColumn,
    sortDirection,
    onSortChange,
    columnFilters,
    onFilterChange,
    canDelete,
    onRefresh,
    onDelete,
    emptyMessage,
}: AdocFileTableProps) {
    const { t } = useTranslations();

    const columns: Array<{ id: string; label: string; sortable: boolean; filterKey?: 'uploaded_by'; align?: 'right' }> = [
        { id: 'doc_title', label: t('adoc.index.table.document', 'Document'), sortable: true },
        { id: 'signed_at', label: t('adoc.index.table.signed_by', 'Signed by'), sortable: true },
        { id: 'entry_count', label: t('adoc.index.table.documents', 'Files'), sortable: false, align: 'right' },
        { id: 'uploaded_by', label: t('adoc.index.table.uploaded_by', 'Uploaded by'), sortable: false, filterKey: 'uploaded_by' },
        { id: 'expires_at', label: t('adoc.index.table.expires', 'Retention'), sortable: true },
        { id: 'actions', label: t('adoc.index.table.actions', 'Actions'), sortable: false, align: 'right' },
    ];

    const filterOptionsFor = (filterKey?: 'uploaded_by'): AdocFilterOption[] => (filterKey === 'uploaded_by' ? users : []);

    const renderRetention = (file: AdocFileSummary) => {
        const days = daysUntil(file.expires_at);
        if (days === null) {
            return <span className="text-muted-foreground text-sm">—</span>;
        }
        if (days < 0) {
            return <span className="text-danger-subtle-foreground text-sm font-medium">{t('adoc.index.retention.expired', 'Expired')}</span>;
        }
        if (days === 0) {
            return (
                <span className="text-warning-subtle-foreground text-sm font-medium">{t('adoc.index.retention.expires_today', 'Expires today')}</span>
            );
        }
        const label = t('adoc.index.retention.expires_in', 'Expires in :days d').replace(':days', String(days));
        return <span className={cn('text-sm', days <= 3 ? 'text-warning-subtle-foreground font-medium' : 'text-muted-foreground')}>{label}</span>;
    };

    return (
        <div className="bg-card mb-4 overflow-x-auto rounded-xl border px-4 py-2.5">
            <Table>
                <TableHeader>
                    <TableRow>
                        {columns.map((column) => {
                            // Columns with neither sort nor filter render a plain label (no empty menu).
                            if (!column.sortable && !column.filterKey) {
                                return (
                                    <TableHead key={column.id} className={cn('px-3', column.align === 'right' && 'text-right')}>
                                        {column.label}
                                    </TableHead>
                                );
                            }
                            const selectedFilters = column.filterKey ? columnFilters[column.filterKey] || [] : [];
                            return (
                                <TableHead key={column.id} className={cn(column.align === 'right' && 'text-right')}>
                                    <TableColumnHeader
                                        label={column.label}
                                        sortDirection={sortColumn === column.id ? sortDirection : null}
                                        onSortAscending={column.sortable ? () => onSortChange(column.id, 'asc') : undefined}
                                        onSortDescending={column.sortable ? () => onSortChange(column.id, 'desc') : undefined}
                                        filterOptions={filterOptionsFor(column.filterKey)}
                                        selectedFilters={selectedFilters}
                                        onFilterChange={column.filterKey ? (values) => onFilterChange(column.filterKey!, values) : undefined}
                                        isFiltered={selectedFilters.length > 0}
                                    />
                                </TableHead>
                            );
                        })}
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {files.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={columns.length} className="text-muted-foreground py-8 text-center">
                                {emptyMessage}
                            </TableCell>
                        </TableRow>
                    ) : (
                        files.map((file) => (
                            <TableRow
                                key={file.id}
                                className="hover:bg-muted/50 cursor-pointer transition-colors"
                                onClick={() => router.visit(route('adoc.show', file.id))}
                            >
                                <TableCell>
                                    <Link
                                        href={route('adoc.show', file.id)}
                                        onClick={(event) => event.stopPropagation()}
                                        className="font-semibold hover:underline"
                                    >
                                        {file.doc_title ?? file.original_name}
                                    </Link>
                                    <div className="text-muted-foreground text-xs">
                                        {file.doc_title ? `${file.original_name} · ${formatBytes(file.file_size)}` : formatBytes(file.file_size)}
                                    </div>
                                </TableCell>
                                <TableCell className="text-muted-foreground text-sm">
                                    {file.signer_name ?? '—'}
                                    {file.signed_at ? <div className="text-xs">{formatDate(file.signed_at)}</div> : null}
                                </TableCell>
                                <TableCell className="text-right text-sm tabular-nums">{file.entry_count}</TableCell>
                                <TableCell className="text-muted-foreground text-sm">{file.uploaded_by ?? '—'}</TableCell>
                                <TableCell>
                                    {renderRetention(file)}
                                    <div className="text-muted-foreground text-xs">{formatDate(file.created_at)}</div>
                                </TableCell>
                                <TableCell onClick={(event) => event.stopPropagation()}>
                                    <div className="flex items-center justify-end gap-1">
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            aria-label={t('adoc.index.actions.refresh', 'Keep 30 more days')}
                                            title={t('adoc.index.actions.refresh', 'Keep 30 more days')}
                                            onClick={() => onRefresh(file)}
                                        >
                                            <RefreshCw />
                                        </Button>
                                        <Button
                                            asChild
                                            variant="ghost"
                                            size="icon"
                                            aria-label={t('adoc.index.actions.download', 'Download original')}
                                        >
                                            <a href={route('adoc.download', file.id)}>
                                                <Download />
                                            </a>
                                        </Button>
                                        {canDelete ? (
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                aria-label={t('adoc.index.actions.delete', 'Delete')}
                                                onClick={() => onDelete(file)}
                                            >
                                                <Trash2 className="text-danger-subtle-foreground" />
                                            </Button>
                                        ) : null}
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </div>
    );
}
